import React, { useState, useMemo, ReactElement } from "react";
import { gql, useMutation, useQuery } from "urql";
import { useLocation, Link } from "wouter";
import { Formik, Form } from "formik";
import { sortBy } from "lodash";
import * as Yup from "yup";

import { emptyObjectId } from "../../../common/src";
import { validIsin } from "../../../common/src/isin-validator";
import { TextField, SelectField, DateField, SubmitButton } from "../../../components/src/form";
import { Alert, AlertVariant } from "../../../components/src/Alert";
import { useAlertTimeOut } from "../../../components/src/common/Utils";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";

import {
    CurrencyEnum,
    InstrumentCategory,
    InstrumentProductTypeEnum,
    InstrumentStatusEnum,
    InstrumentModelTypeEnum,
    InstrumentModelInput,
    InstrumentModelLegTypesEnum,
    DayAdjustmentEnum,
    CreateInstrumentInput,
    CicAreaEnum,
    CicTypeEnum
} from "../types.generated";
import { SYSTEM_PARTY_ID } from "../Constants";

const GET_PLACES_OF_SETTLEMENT = gql`
    query {
        places: parties(filter: { typeIn: PlaceOfSettlement }) {
            _id
            name
        }
    }
`;

const CREATE_INSTRUMENT = gql`
    mutation createInstrument($input: CreateInstrumentInput!) {
        createInstrument(input: $input) {
            _id
        }
    }
`;

interface FormValues {
    name: string;
    currency: CurrencyEnum;
    isin: string;
    maturityDate: string;
    placeOfSettlementId: string;
}

const defaultFormData: FormValues = {
    name: "",
    currency: CurrencyEnum.SEK,
    isin: "",
    maturityDate: "",
    placeOfSettlementId: emptyObjectId
};

const DEFAULT_PLACE_OF_SETTLEMENT_NAME = "Euroclear Sweden";

export function ZeroCouponLinearInstrumentPage(): ReactElement {
    const [_, navigate] = useLocation();
    const [__, createInstrument] = useMutation(CREATE_INSTRUMENT);
    const [{ data: partiesData }] = useQuery({ query: GET_PLACES_OF_SETTLEMENT });

    const places: { _id: string; name: string }[] = useMemo(() => sortBy(partiesData?.places || [], "name"), [partiesData]);

    const placeOfSettlementOptions = useMemo(
        () => [{ key: emptyObjectId, value: "None" }, ...places.map((d) => ({ key: d._id, value: d.name }))],
        [places]
    );

    // Default to Euroclear Sweden once the places have loaded, fall back to None if it is missing
    const initialValues: FormValues = useMemo(() => {
        const defaultPlace = places.find((d) => d.name === DEFAULT_PLACE_OF_SETTLEMENT_NAME);
        return { ...defaultFormData, placeOfSettlementId: defaultPlace ? defaultPlace._id : emptyObjectId };
    }, [places]);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    return (
        <div className="mx-auto px-4 py-4">
            <div className="mb-2">
                <Link to={"/instruments"}>Instruments</Link>
            </div>
            <h4>New Zero Coupon Linear Instrument</h4>
            {alert.visible ? (
                <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                    {alert.message}
                </Alert>
            ) : null}
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={Yup.object({
                    name: Yup.string().required("Name is required").min(2, "Name must be at least 2 characters"),
                    currency: Yup.string().required("Currency is required"),
                    isin: Yup.string()
                        .nullable()
                        .test("test-isin", "Invalid ISIN format", function (value) {
                            if (!value) {
                                return true;
                            }
                            const result = validIsin(value, { checkCountryCode: false });
                            if (result[0]) {
                                return true;
                            } else {
                                return this.createError({ message: result[1].message });
                            }
                        }),
                    maturityDate: Yup.string().required("Maturity Date is required")
                })}
                onSubmit={async (values, { setSubmitting, setErrors }) => {
                    // Build the ZeroCouponLinear model structure
                    const model: InstrumentModelInput = {
                        _t: InstrumentModelTypeEnum.ZeroCouponLinear,
                        accountId: null,
                        clientId: null,
                        expiryDate: null,
                        legs: [
                            {
                                _t: InstrumentModelLegTypesEnum.FixedAmountLeg,
                                calendars: ["CaptorSweden"],
                                call: null,
                                creditSpread: null,
                                currency: values.currency,
                                dayAdjustment: DayAdjustmentEnum.FixedPeriods,
                                dayCount: null,
                                exercise: null,
                                expiry: null,
                                fixingType: null,
                                flatAdjustment: null,
                                issuerId: emptyObjectId,
                                longStub: null,
                                maturityDate: values.maturityDate,
                                maturityPeriod: null,
                                multiplier: null,
                                notional: null,
                                payLeg: false,
                                rate: null,
                                rateIndexId: null,
                                recoveryRate: 1,
                                relativeAdjustment: null,
                                rollingDate: null,
                                rollingPeriod: null,
                                settlementType: null,
                                spread: null,
                                startDate: null,
                                startDateOffset: null,
                                startPeriod: null,
                                strike: null,
                                ultimateForwardRate: null,
                                underlyingId: null,
                                volatility: null,
                                zeroFloor: null,
                                cashFlows: [
                                    {
                                        endDate: null,
                                        exDate: values.maturityDate,
                                        fixings: null,
                                        notional: 1,
                                        payDate: values.maturityDate,
                                        startDate: null
                                    }
                                ],
                                underlyings: null
                            }
                        ],
                        objectId: null,
                        objectType: null,
                        notionalScaling: 1,
                        quoteCurrency: values.currency,
                        quoteType: "Clean",
                        startDate: null
                    };

                    const input: CreateInstrumentInput = {
                        name: values.name,
                        clientIds: [SYSTEM_PARTY_ID],
                        currency: values.currency,
                        isin: values.isin,
                        longName: values.name,
                        status: InstrumentStatusEnum.Confirmed,
                        category: InstrumentCategory.InterestRate,
                        productType: InstrumentProductTypeEnum.Bond,
                        bloombergQuoteMultiplier: 1.0,
                        cicArea: CicAreaEnum.SE,
                        cicType: CicTypeEnum.XX23,
                        placeOfSettlementId: values.placeOfSettlementId,
                        model: model
                    };

                    await createInstrument({ input })
                        .then((result) => {
                            if ("error" in result && result.error) {
                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                setAlert({ color: "danger", visible: true, message });
                            } else {
                                const newInstrId = result.data.createInstrument._id;
                                setAlert({
                                    color: "success",
                                    visible: true,
                                    message: `New instrument '${values.name}' created successfully!`
                                });
                                // redirect to edit instrument page on success
                                if (newInstrId) {
                                    navigate(`/instruments/${newInstrId}`, { replace: true });
                                }
                            }
                            return true;
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <div className="flex flex-wrap -mx-2">
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <TextField name="name" label="Name" className="" disabled={isSubmitting} />
                            </div>
                            <div className="w-full md:w-[5.56%] min-w-[100px] px-2 mb-4">
                                <SelectField
                                    name="currency"
                                    label="Currency"
                                    className=""
                                    disabled={isSubmitting}
                                    options={Object.keys(CurrencyEnum).sort()}
                                />
                            </div>
                            <div className="w-full md:w-1/12 px-2 mb-4">
                                <TextField name="isin" label="ISIN" className="" disabled={isSubmitting} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <DateField name="maturityDate" label="Maturity Date" className="" disabled={isSubmitting} />
                            </div>
                            <div className="w-full md:w-4/12 px-2 mb-4">
                                <SelectField
                                    name="placeOfSettlementId"
                                    label="Place of settlement"
                                    className=""
                                    disabled={isSubmitting}
                                    options={placeOfSettlementOptions}
                                />
                            </div>
                        </div>
                        <div className="mt-4">
                            <SubmitButton disabled={isSubmitting} label="Create Instrument" />
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

